import { useEffect, useMemo, useState } from "react";
import { Alert, Box, IconButton, LinearProgress, Stack, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import { Refresh } from "@mui/icons-material";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { getUserCompanies } from "@/api/user.service";
import type { Company } from "@/api/company.service";
import { CrudPage } from "@/components/crud/CrudPage";
import {
  ABAS,
  agrupadorDaAba,
  DIMENSAO_LABELS,
  filtroDaLinha,
  getRupturaGeral,
  type RupturaDimensao,
  type RupturaFiltros,
  type RupturaProduto,
  type RupturaResumo,
} from "@/api/ruptura.service";
import { GeralCard, ResumoCard } from "./RupturaCards";
import { SeletorLojas } from "./SeletorLojas";
import { DetalheView } from "./RupturaDetalheView";
import { ProdutosView, type EstadoLista } from "./RupturaProdutosView";
import { formatDataHora, SEM_FILTRO, useAosPoucos, type AbrirProdutos, type NomeLoja } from "./rupturaComum";
import { CacheRupturaContext, LojasRupturaContext, useRupturaBusca, useRupturaResumo, type CacheRuptura } from "./useRupturaBusca";

// ponytail: mesma navegação de app-mobile/src/app/ruptura.tsx → rupturaProdutos.tsx →
// rupturaProdutoDetalhe.tsx, com os cards no padrão do Flash (HierarchyPanel). As três
// telas ficam montadas e só a atual aparece, para a volta manter abertos/paginação.

// Estado da tela na URL, para o "voltar" do navegador (ex.: vindo da Consulta de Produtos pelo
// link do produto) reabrir a tela como estava: aba, lojas tiradas, lista com filtros/busca e detalhe.
// Abrir a lista ou o detalhe empilha uma entrada no histórico; o resto só substitui a atual.
const CAMPOS_FILTRO = [
  "cod_empresa",
  "cod_categoria",
  "cod_comprador",
  "cod_fornecedor",
  "cod_problema",
  "cod_responsavel",
  "cod_solucao",
] as const satisfies readonly (keyof RupturaFiltros)[];
const CAMPOS_NUMERICOS = new Set<keyof RupturaFiltros>(["cod_problema", "cod_responsavel", "cod_solucao"]);
const PARAMS_LISTA = ["produtos", "busca", "agrupar", "detalhe_produto", "detalhe_loja", ...CAMPOS_FILTRO];

const lerFiltros = (params: URLSearchParams): RupturaFiltros =>
  Object.fromEntries(
    CAMPOS_FILTRO.flatMap((campo) => {
      const valor = params.get(campo);
      if (valor === null) return [];
      return [[campo, CAMPOS_NUMERICOS.has(campo) ? Number(valor) : valor]];
    }),
  );

const escreverFiltros = (params: URLSearchParams, filtros: RupturaFiltros) => {
  for (const campo of CAMPOS_FILTRO) {
    const valor = filtros[campo];
    if (valor === null || valor === undefined) params.delete(campo);
    else params.set(campo, String(valor));
  }
};

const fecharLista = (params: URLSearchParams) => PARAMS_LISTA.forEach((p) => params.delete(p));

export function RupturaPage() {
  // Cache das buscas da tela; o "Recarregar" troca por um vazio.
  const [cache, setCache] = useState<CacheRuptura>(() => new Map());

  return (
    <CacheRupturaContext.Provider value={cache}>
      <RupturaTela onLimparCache={() => setCache(new Map())} />
    </CacheRupturaContext.Provider>
  );
}

function RupturaTela({ onLimparCache }: { onLimparCache: () => void }) {
  const { user } = useAuth();
  const userId = user?.id;
  const clientId = user?.loggedClient?.id;

  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const dimensao: RupturaDimensao = ABAS.find((a) => a === params.get("aba")) ?? "loja";
  const [versao, setVersao] = useState(0);
  // Lojas tiradas no seletor "Lojas" (erp_code); todas as outras entram nas buscas.
  const semLojas = params.get("sem_lojas") ?? "";
  const lojasTiradas = useMemo(() => new Set(semLojas.split(",").filter(Boolean)), [semLojas]);
  // null = ainda carregando: as buscas esperam, porque dependem das lojas.
  const [companies, setCompanies] = useState<Company[] | null>(null);
  // Filtros da lista de produtos aberta (null = lista fechada).
  const produtosDe = params.get("produtos") ? lerFiltros(params) : null;
  // Remonta a lista só quando ela é aberta de novo por um atalho, não a cada filtro trocado nela.
  const [aberturaLista, setAberturaLista] = useState(0);
  const detalheProduto = params.get("detalhe_produto");
  const detalheLoja = params.get("detalhe_loja");
  // Linha clicada na lista, para o detalhe já aparecer enquanto carrega.
  const [linhaDetalhe, setLinhaDetalhe] = useState<RupturaProduto | null>(null);

  const mudarUrl = (alterar: (p: URLSearchParams) => void, empilhar = false) =>
    setParams(
      (atual) => {
        const prox = new URLSearchParams(atual);
        alterar(prox);
        return prox;
      },
      empilhar ? { state: { rupturaEmpilhado: true } } : { replace: true, state: location.state },
    );

  // Se a entrada atual foi empilhada pela própria tela, volta no histórico; senão (aberta por
  // link ou recarregada), só fecha, sem sair da página.
  const voltar = (alterar: (p: URLSearchParams) => void) => {
    if (location.state?.rupturaEmpilhado) navigate(-1);
    else mudarUrl(alterar);
  };

  useEffect(() => {
    if (!userId || !clientId) return;
    getUserCompanies(userId)
      .then((data) => setCompanies(data.filter((c) => c.client_id === clientId)))
      .catch(() => setCompanies([]));
  }, [userId, clientId]);

  // O nome da loja não é gravado nas tabelas de ruptura: resolve-se pelo
  // cod_empresa (erp_code) nas empresas do usuário logado no cliente.
  const nomeLoja = useMemo<NomeLoja>(
    () => (codEmpresa) => (codEmpresa ? ((companies ?? []).find((c) => c.erp_code === codEmpresa)?.name ?? codEmpresa) : "—"),
    [companies],
  );

  const lojas = useMemo(
    () => (companies ?? []).filter((c) => !lojasTiradas.has(c.erp_code)).map((c) => c.erp_code),
    [companies, lojasTiradas],
  );

  // Sem o geral a tela segue, só sem o card do topo.
  const geral = useRupturaBusca(
    clientId && lojas.length > 0 ? `geral:${clientId}:${lojas.join(",")}` : null,
    () => getRupturaGeral(clientId!, lojas),
    "Não foi possível carregar a ruptura.",
  );
  // Na aba Problema o primeiro nível é o responsável; os problemas abrem dentro dele.
  const agrupador = agrupadorDaAba(dimensao);
  const resumo = useRupturaResumo(agrupador, SEM_FILTRO, clientId, lojas);
  const linhas = resumo.dados ?? [];
  const cards = useAosPoucos(linhas, `${versao}-${dimensao}`);
  // Espera os dois: o card geral não aparece depois, empurrando os demais.
  const loading = companies === null || geral.loading || resumo.loading;

  if (!clientId) {
    return (
      <CrudPage title="Gestão de Ruptura">
        <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
          Selecione um cliente para ver a ruptura.
        </Typography>
      </CrudPage>
    );
  }

  const nomeLinha = (linha: RupturaResumo) => (linha.dimensao === "loja" ? nomeLoja(linha.chave) : linha.rotulo);

  // Atalho de um card: a linha vira mais um filtro da lista.
  const abrirLista = (filtros: RupturaFiltros) => {
    setAberturaLista((n) => n + 1);
    mudarUrl((p) => {
      fecharLista(p);
      escreverFiltros(p, filtros);
      p.set("produtos", "1");
    }, true);
  };
  const abrirProdutos: AbrirProdutos = (linha, filtros) => abrirLista({ ...filtros, ...(linha && filtroDaLinha(linha)) });

  const mudarLista = ({ filtros, busca, agrupar }: EstadoLista) =>
    mudarUrl((p) => {
      escreverFiltros(p, filtros);
      if (busca) p.set("busca", busca);
      else p.delete("busca");
      if (agrupar) p.set("agrupar", "1");
      else p.delete("agrupar");
    });

  const abrirDetalhe = (produto: RupturaProduto) => {
    setLinhaDetalhe(produto);
    mudarUrl((p) => {
      p.set("detalhe_produto", produto.cod_produto);
      p.set("detalhe_loja", produto.cod_empresa);
    }, true);
  };

  const trocarAba = (aba: RupturaDimensao) =>
    mudarUrl((p) => {
      if (aba === "loja") p.delete("aba");
      else p.set("aba", aba);
    });

  // Trocar as lojas muda todos os números: fecha a lista de produtos aberta.
  const trocarLojas = (tiradas: Set<string>) =>
    mudarUrl((p) => {
      fecharLista(p);
      if (tiradas.size > 0) p.set("sem_lojas", [...tiradas].join(","));
      else p.delete("sem_lojas");
    });

  const recarregar = () => {
    onLimparCache();
    mudarUrl(fecharLista);
    setVersao((v) => v + 1);
  };

  return (
    <LojasRupturaContext.Provider value={lojas}>
      <CrudPage
        title="Gestão de Ruptura"
        subtitle={`Apurado em ${formatDataHora(geral.dados?.data_apuracao)}`}
        headerActions={
          <Tooltip title="Recarregar">
            <IconButton size="small" onClick={recarregar}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        }
      >
        <Box sx={{ display: produtosDe ? "none" : "block" }}>
          <Stack gap={2}>
            <Stack direction="row" alignItems="center" gap={2}>
              <Tabs value={dimensao} onChange={(_, v) => trocarAba(v)} variant="scrollable" scrollButtons="auto" sx={{ flex: 1, minWidth: 0 }}>
                {ABAS.map((d) => (
                  <Tab key={d} value={d} label={DIMENSAO_LABELS[d]} />
                ))}
              </Tabs>
              <SeletorLojas lojas={companies ?? []} desmarcadas={lojasTiradas} onChange={trocarLojas} />
            </Stack>

            {resumo.erro && <Alert severity="error">{resumo.erro}</Alert>}
            {loading ? (
              <LinearProgress />
            ) : linhas.length === 0 && !resumo.erro ? (
              <Typography color="text.secondary" textAlign="center" sx={{ py: 6 }}>
                {lojas.length === 0 ? "Nenhuma loja selecionada." : "Nenhum dado encontrado para esta dimensão."}
              </Typography>
            ) : (
              <Stack gap={1}>
                {geral.dados && geral.dados.qtd_total > 0 && (
                  <GeralCard
                    key={`${versao}-${dimensao}`}
                    nome={user?.loggedClient?.name ?? "Total"}
                    geral={geral.dados}
                    valorRisco={linhas.reduce((soma, l) => soma + l.valor_risco, 0)}
                    aba={agrupador}
                    clientId={clientId}
                    nomeLoja={nomeLoja}
                    onVerProdutos={abrirProdutos}
                    onVerTodos={() => abrirLista(SEM_FILTRO)}
                  />
                )}
                {cards.visiveis.map((linha) => (
                  <ResumoCard
                    key={`${versao}-${dimensao}-${linha.chave}`}
                    item={linha}
                    nome={nomeLinha(linha)}
                    aba={agrupador}
                    clientId={clientId}
                    nomeLoja={nomeLoja}
                    onVerProdutos={abrirProdutos}
                  />
                ))}
                {cards.temMais && <Box ref={cards.fimRef} sx={{ height: 1 }} />}
              </Stack>
            )}
          </Stack>
        </Box>

        {produtosDe && (
          <Box sx={{ display: detalheProduto ? "none" : "block" }}>
            <ProdutosView
              key={aberturaLista}
              filtrosIniciais={produtosDe}
              buscaInicial={params.get("busca") ?? ""}
              agruparInicial={params.get("agrupar") === "1"}
              clientId={clientId}
              nomeLoja={nomeLoja}
              onVoltar={() => voltar(fecharLista)}
              onVerDetalhe={abrirDetalhe}
              onMudar={mudarLista}
            />
          </Box>
        )}

        {produtosDe && detalheProduto && detalheLoja && (
          <DetalheView
            key={`${detalheLoja}-${detalheProduto}`}
            codProduto={detalheProduto}
            codEmpresa={detalheLoja}
            produto={
              linhaDetalhe?.cod_produto === detalheProduto && linhaDetalhe.cod_empresa === detalheLoja ? linhaDetalhe : undefined
            }
            clientId={clientId}
            nomeLoja={nomeLoja}
            onVoltar={() =>
              voltar((p) => {
                p.delete("detalhe_produto");
                p.delete("detalhe_loja");
              })
            }
          />
        )}
      </CrudPage>
    </LojasRupturaContext.Provider>
  );
}
